import React, { useState } from 'react';
import { Business } from '../models';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  businessId: string;
  createdAt: Date;
  updatedAt: Date;
}

interface ProductDetailViewProps {
  product: Product;
  business: Business;
  isOpen: boolean;
  onClose: () => void;
}

const ProductDetailView: React.FC<ProductDetailViewProps> = ({ product, business, isOpen, onClose }) => {
  const [selectedImageIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);

  if (!isOpen) return null;

  // For now, we'll use the product image as a single image
  // In the future, this could be expanded to support multiple images like iOS
  const productImages = product.imageUrl ? [product.imageUrl] : [];

  return (
    <>
      {/* Main Product Detail Modal */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        zIndex: 2000,
        backdropFilter: 'blur(10px)',
        paddingTop: '20px',
        overflowY: 'auto'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          marginBottom: '20px'
        }}>
          {/* Header with Close Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
            >
              ×
            </button>
          </div>

          {/* Product Images */}
          <div style={{ marginBottom: '24px' }}>
            {productImages.length > 0 ? (
              <div style={{
                width: '100%',
                borderRadius: '16px',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#f0f0f0',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                aspectRatio: '4/3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={productImages[selectedImageIndex]}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullscreen(true)}
                />

                {/* Click to fullscreen hint */}
                <div style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  opacity: 0.8
                }}>
                  Tap to zoom
                </div>
              </div>
            ) : (
              <div style={{
                width: '100%',
                height: '200px',
                backgroundColor: '#f0f0f0',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
              }}>
                <span style={{ fontSize: '60px', color: '#999' }}>📦</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {product.name}
            </h2>

            <div style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#007aff',
              marginBottom: '16px'
            }}>
              ${product.price.toFixed(2)}
            </div>

            {product.description && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Description
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.5'
                }}>
                  {product.description}
                </p>
              </div>
            )}

            {/* Business Info */}
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
              }}>
                Sold by
              </h3>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {business.imageUrl ? (
                  <img
                    src={business.imageUrl}
                    alt={business.name}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#e9ecef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    🏢
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '4px'
                  }}>
                    {business.name}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '4px'
                  }}>
                    {business.category}
                  </div>
                  {business.phoneNumber && (
                    <div style={{
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      📞 {business.phoneNumber}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    // Could open business detail view or navigate to business
                    console.log('View business:', business.name);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '50%',
                    color: '#666',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                    e.currentTarget.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#666';
                  }}
                >
                  <span style={{ fontSize: '16px' }}>→</span>
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            <button
              onClick={() => {
                if (business.phoneNumber) {
                  window.open(`tel:${business.phoneNumber}`, '_self');
                }
              }}
              style={{
                flex: 1,
                padding: '16px',
                backgroundColor: business.phoneNumber ? '#007aff' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: business.phoneNumber ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              disabled={!business.phoneNumber}
              onMouseEnter={(e) => {
                if (business.phoneNumber) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              📞 Call to Order
            </button>

            <button
              onClick={() => {
                const lat = business.location.latitude;
                const lng = business.location.longitude;
                const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                window.open(url, '_blank');
              }}
              style={{
                flex: 1,
                padding: '16px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              🗺️ Directions
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {showFullscreen && productImages.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000,
          cursor: 'pointer'
        }}
        onClick={() => setShowFullscreen(false)}
        >
          <div style={{
            maxWidth: '90%',
            maxHeight: '90%',
            position: 'relative'
          }}>
            <img
              src={productImages[selectedImageIndex]}
              alt={product.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />

            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFullscreen(false);
              }}
              style={{
                position: 'absolute',
                top: '-50px',
                right: '0',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetailView;
